"use client";

import { FC, useCallback, useContext, useState } from "react";
import { useTranslation } from "react-i18next";
import { Controller, useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import classNames from "classnames";
import { ClientBFFContext, changePasswordValidationSchema } from "@finbackoffice/site-core";
import { RequestError } from "@finbackoffice/fe-core";
import { AssetsDirectory, SignupInputFieldName, TranslationScopes } from "@finbackoffice/enums";
import { InferType } from "yup";
import Button from "components/base/button/Button";
import { useNotification, useSignupConfigV2 } from "hooks";
import Translate from "components/base/translate/Translate";
import Img from "components/base/img/Img";
import Input from "components/base/input-field/Input";
import AccountDetailsFormBlock from "components/account/details/form-block/AccountDetailsFormBlock";
import { Svg } from "components/base/svg/Svg";
import styles from "./change-password-form.module.sass";

const ChangePasswordForm: FC = () => {
    const client = useContext(ClientBFFContext);
    const { t } = useTranslation(TranslationScopes.UserAccount);

    const { signupConfigObj: changePassConfigObj } = useSignupConfigV2([
        SignupInputFieldName.Password,
    ]);
    const { notification, setNotification, clearNotification } = useNotification();

    const [showOldPassword, setShowOldPassword] = useState(false);
    const [showNewPassword, setShowNewPassword] = useState(false);
    const [showConfirmPassword, setShowConfirmPassword] = useState(false);
    const [loading, setLoading] = useState(false);
    const [success, setSuccess] = useState(false);

    const {
        control,
        handleSubmit,
        reset,
        formState: { errors, isSubmitting, isValid, isDirty },
    } = useForm({
        mode: "onChange",
        context: {
            configObj: {
                ...changePassConfigObj,
                t,
            },
        },
        resolver: yupResolver(changePasswordValidationSchema),
        defaultValues: {
            password: "",
            new_password: "",
            password_confirm: "",
        },
    });

    const onSubmit = useCallback(
        async (data: InferType<typeof changePasswordValidationSchema>) => {
            if (isDirty && isValid && !isSubmitting && !loading) {
                setLoading(true);
                try {
                    await client.changePassword({
                        old_password: data.password as string,
                        new_password: data.new_password as string,
                    });
                    setSuccess(true);
                    reset();
                    setLoading(false);
                } catch (err: any) {
                    const error: RequestError = err.response?.data;

                    setNotification({
                        type: "error",
                        error: error.error,
                        message:
                            typeof error.message === "string" ? error.message : error.message[0],
                    });

                    setLoading(false);
                }
            }
        },
        [client, isDirty, isSubmitting, isValid, loading, reset, setNotification],
    );

    const handleCancel = useCallback(() => {
        reset();
        if (notification.type) {
            clearNotification();
        }
    }, [clearNotification, notification.type, reset]);

    const renderLabel = useCallback(
        () => (
            <>
                <Svg
                    src={`/${AssetsDirectory.Common}/${AssetsDirectory.Mobile}/${AssetsDirectory.BaseIcons}/account-details-password.svg`}
                    className="svg-icon"
                />
                <span>
                    <Translate
                        tid="account_details_change_password"
                        namespace={TranslationScopes.UserAccount}
                    />
                </span>
            </>
        ),
        [],
    );

    return (
        <AccountDetailsFormBlock
            wrapperClassname={styles.changePassword}
            renderHeader={renderLabel}
            testId="change-password-form"
            notification={notification}>
            <form onSubmit={handleSubmit(onSubmit)}>
                <div className={styles.blockContent}>
                    <Controller
                        render={({ field: { onChange, value, name } }) => {
                            return (
                                <Input
                                    onChange={onChange}
                                    value={value}
                                    name={name}
                                    label={t("account_details_old_password")}
                                    innerIconClass={classNames(
                                        "viewPass",
                                        showOldPassword && "showPass",
                                    )}
                                    type={showOldPassword ? "text" : "password"}
                                    onInnerIconClick={() => setShowOldPassword(!showOldPassword)}
                                    error={errors.password}
                                />
                            );
                        }}
                        control={control}
                        name="password"
                    />

                    <Controller
                        render={({ field: { onChange, value, name } }) => {
                            return (
                                <Input
                                    onChange={onChange}
                                    value={value}
                                    name={name}
                                    label={t("account_details_new_password")}
                                    innerIconClass={classNames(
                                        "viewPass",
                                        showNewPassword && "showPass",
                                    )}
                                    type={showNewPassword ? "text" : "password"}
                                    onInnerIconClick={() => setShowNewPassword(!showNewPassword)}
                                    error={errors.new_password}
                                />
                            );
                        }}
                        control={control}
                        name="new_password"
                    />

                    <Controller
                        render={({ field: { onChange, value, name } }) => {
                            return (
                                <Input
                                    onChange={onChange}
                                    value={value}
                                    name={name}
                                    label={t("account_details_confirm_new_password")}
                                    innerIconClass={classNames(
                                        "viewPass",
                                        showConfirmPassword && "showPass",
                                    )}
                                    type={showConfirmPassword ? "text" : "password"}
                                    onInnerIconClick={() =>
                                        setShowConfirmPassword(!showConfirmPassword)
                                    }
                                    error={errors.password_confirm}
                                />
                            );
                        }}
                        control={control}
                        name="password_confirm"
                    />

                    <p>
                        <Svg
                            src={`/${AssetsDirectory.Common}/${AssetsDirectory.Mobile}/${AssetsDirectory.BaseIcons}/account-details-warning.svg`}
                            className="svg-icon"
                        />
                        <Translate
                            tid="account_details_change_password_txt"
                            namespace={TranslationScopes.UserAccount}
                        />
                    </p>
                    {success && (
                        <div className={styles.passwordChanged}>
                            <span>
                                <Img
                                    source={`/${AssetsDirectory.Common}/${AssetsDirectory.Mobile}/${AssetsDirectory.BaseIcons}/account-verified.svg`}
                                    alt="password changed"
                                    width={12}
                                    height={10}
                                />
                                <Translate
                                    tid="account_details_change_password_successfully_changed"
                                    namespace={TranslationScopes.UserAccount}
                                />
                            </span>
                            <Button
                                type="button"
                                variant="primary"
                                onClick={() => setSuccess(false)}>
                                <Translate tid="base_ok" />
                            </Button>
                        </div>
                    )}
                </div>
                <div className={styles.blockFooter}>
                    <Button
                        type="button"
                        variant="secondary"
                        onClick={handleCancel}
                        disabled={!isDirty || loading}>
                        <span>
                            <Svg
                                src={`/${AssetsDirectory.Common}/${AssetsDirectory.Mobile}/${AssetsDirectory.BaseIcons}/close.svg`}
                                className="svg-icon"
                            />
                            <Translate tid="base_cancel" />
                        </span>
                    </Button>
                    <Button
                        type="submit"
                        variant="primary"
                        disabled={
                            !isDirty ||
                            isSubmitting ||
                            !isValid ||
                            loading ||
                            !!errors.root?.apiError ||
                            !!notification.type
                        }>
                        <span>
                            <Svg
                                src={`/${AssetsDirectory.Common}/${AssetsDirectory.Mobile}/${AssetsDirectory.BaseIcons}/account-save.svg`}
                                className="svg-icon"
                            />

                            <Translate tid="base_save" />
                        </span>
                    </Button>
                </div>
            </form>
        </AccountDetailsFormBlock>
    );
};
export default ChangePasswordForm;
